import { comparer, toJS, observable, isObservable } from 'mobx'
import { v4 as uuid } from 'uuid'
import defaultSizes from '../../utils/default-sizes'
import generalProps from '../../utils/general-props'
import Field from './field'
import Mapping, { MappingItem, forType as createMappingForType, IMappingItemSnapshot } from './mapping'
import Auditable from './auditable'
import ref from './reference'
import { nanoid as shortid } from 'nanoid'
import {
  types as t,
  getPath,
  getRoot,
  hasParent,
  getParent,
  getSnapshot,
  isStateTreeNode,
  getEnv,
  IAnyModelType,
  IArrayType,
  IMaybeNull,
  Instance
} from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Position from './position'
import docGen from 'component-index/docgen.json'
import pick from 'lodash/pick'
import get from 'lodash/get'
import set from 'lodash/set'
import mergeWith from 'lodash/mergeWith'
import sortBy from 'lodash/sortBy'
import ssrProps from '../../utils/ssr-props'

import { dataSourcesFieldSets } from '../../utils/deptree'
import type { IStore } from '../store'

const RefMapping = ref(t.late(() => MappingItem), 'mapping')

export const Component = t
  .model('ComponentItem', {
    id: t.identifier,
    path: t.maybeNull(t.string),
    type: t.maybeNull(t.string),
    name: t.maybeNull(t.string),
    notes: t.maybeNull(t.string),
    components: t.late((): IMaybeNull<IArrayType<IAnyModelType>> => (t.optional(t.array(Component), [] as IAnyModelType[]) as any)),
    position: t.optional(Position, { x: 0, y: 0, w: 12, h: 2 }),
    props: t.optional(t.frozen(), {}),
    code: t.maybeNull(t.string),
    bundleUrl: t.maybeNull(t.string),
    ccId: t.maybeNull(t.string),
    mapping: RefMapping,
    compactType: t.maybeNull(t.string)
  })
  .volatile(self => ({
    _state: observable.box(undefined, { equals: comparer.structural, deep: false }),
    _defaultMapping: null,
    _ref: observable.box(),
    _renderProps: observable.box(undefined, { equals: comparer.structural, deep: false })
  }))
  .actions(self => ({
    setDefaultMapping (mapping) {
      self._defaultMapping = mapping
    },
    setState (newState) {
      self._state.set(self._state.get()
        ? Object.assign({}, self._state.get(), newState)
        : newState)
    },
    setRef (ref) {
      self._ref.set(ref)
    },
    setRenderProps (props) {
      self._renderProps.set(props)
    },
    createFromPropTypes: (doc, ignoreDefaults = true) => {
      if (doc) {
        const mapping: IMappingItemSnapshot = createMappingForType({
          ...doc
        }, undefined, undefined, ignoreDefaults);
        mapping.children = [...mapping.children || [], ...generalProps]
        mapping.comp = self.id
        return mapping
      }
      return null
    },
  }))
  .views(self => ({
    get ref () {
      return self._ref.get()
    },
    get componentsOrderedLtr () {
      // used for vertical compact
      if(!self.components) return;
      var newcomps = [];
      for(let a of self.components) {
        let i = 0;
        for(let b of newcomps) {
          const {y: ay, h: ah, x: ax} = a.position;
          const {y: by, h: bh, x: bx} = b.position;
          
          if (ay + ah <= by) break;
          if (ay <= by && ax < bx) break;
          i++;
        }
        newcomps.splice(i, 0, a);
      }
      return newcomps;
    },
    get defaultMapping () {
      if (!self._defaultMapping) {
        const doc = docGen[self.type]
        const m = self.createFromPropTypes(doc, false)
        self.setDefaultMapping(m)
      }
      return self._defaultMapping
    },
    clientExport () {
      return {
        ...getSnapshot(self),
        components: self.components.map(c => c.clientExport())
      }
    },
    get htmlId () {
      return `iar_${self.id}`
    },
    get state () {
      return self._state.get() || {}
    },
    get renderProps () {
      const props = self._renderProps.get()
      if (!props) return props
      const keys = (
        [...self.mapping?.children?.map(c => c.key) || [],
        ...Object.keys(getEnv(self)?.props?.[self.id] || {})
      ]).filter(k => k !== 'children')
      if (!keys.length) return {}
      return pick(props, keys)
    },
    get toProps () {
      return ssrProps(self.id, getEnv(self)?.props, self.mapping?.mapper?.toProps)
    }
  }), { componentsOrderedLtr: { memoize: true }})
  .actions(self => {
    return {
      afterCreate () {
        Object.defineProperties(self, {
          mapper: {
            get: function () {
              return self.mapping && self.mapping.mapper
            }
          }
        })
      }
    }
  })
  .views(self => ({
    get index () {
      return (self?.components as any[])?.findIndex?.(c => c === self)
    },
    get fullKey () {
      let parent = self
      const keys = []
      while (parent && Component.is(parent)) {
        const key = parent.name || parent.type
        keys.push(key)
        parent = (parent as any).parent as typeof self
      }
      return keys.reverse().join('.')
    },
    getSnapshotWithNewIds (suffix = '') {
      const snap = {
        ...getSnapshot(self),
        id: suffix ? self.id + ':' + suffix : shortid(),
        components:
          self.components && self.components.map(c => c.getSnapshotWithNewIds(suffix))
      }
      if (self.type === 'Custom' && !snap.ccId) snap.ccId = self.id
      if (self.mapping && isStateTreeNode(self.mapping)) {
        const clonedMapping = {
          ...getSnapshot(self.mapping),
          id: suffix ? (self.mapping as any).id + ':' + suffix : shortid(),
          comp: snap.id
        }
        snap.mapping = clonedMapping
      }
      return snap
    },
    get maxY () {
      let max = Math.max.apply(
        Math,
        (self.components &&
          self.components.map(c => c.position.y + c.position.h)) ||
        []
      )
      if (max < 0) max = 1
      return max
    },
    get parent () {
      return hasParent(self, 2) ? getParent(self, 2) : null
    },
    get parentNodes () {
      let parent = self
      const root = getRoot(self)
      const nodes = [self]
      while (parent && parent !== root) {
        parent = hasParent(self, 2) ? getParent(parent, 2) : null
        if (Component.is(parent)) nodes.unshift(parent)
      }
      return nodes
    },
    get completeFieldSet () {
      return (self as any).getCompleteFieldSet()
    },
    get allDataSources () {
      return Array.from((self as any).getAllDataSources())
    },
    getAllDataSources (all = new Set()) {
      self.mapping?.getAllDataSources(all)
      for (const c of self.components) c.getAllDataSources(all)
      return all
    },
    get areDataSourcesLoading () {
      const root = getRoot(self)
      if (!(root as any).dataSources) return

      for (const dsId of (self as any).allDataSources) {
        const ds = (root as any).dataSources[dsId]
        if (ds && ds.isLoading) return true
      }
      return false
    },
    get hasChildren () {
      if (self.type === 'Page') return true
      if (self.mapping && self.mapping.children) {
        return self.mapping.children.find(c => c.key === 'children') != null
      }
      return false
    },
    get isResizable () {
      return (
        self.mapping &&
        self.mapping.children.find(
          c => c.key === 'width' || c.key === 'height'
        ) != null
      )
    },
    get mainParent () {
      if (!(self as any)?.parent.id) return self
      return (self as any).parent.mainParent
    },
    get gridPosition () {
      return self.position
    },
    get urllink () {
      return '/admin' + getPath(self)
    },
    get link () {
      if (!(self as any).parent.components) return null
      return (
        (((self as any).parent.link && (self as any).parent.link + ' > ') || '') +
        (self.name || self.type)
      )
    },
    fetchChildNodes: async node => {
      const self = node.meta && node.meta.component
      if (node.key === 'parent') {
        return (
          (self.parent &&
            self.parent.labels &&
            self.parent.labels().childNodes) ||
          []
        )
      } else if (node.key === 'children') {
        return self.components.map(c => c.labels()) || []
      }
    },
    get label () {
      if (!self.name) return self.type
      return self.name
    },
    labels (comp = {}) {
      const props = self.mapping && {
        label: 'props',
        key: 'props',
        meta: { component: self },
        childNodes:
          self.mapping && self.mapping.children
            ? self.mapping.children.map(c => c.labels(comp)).filter(c => c)
            : []
      }
      const state = self.mapping && {
        label: 'state',
        key: 'state',
        meta: { component: self, fieldPath: 'state' },
        childNodes: self.state ? Object.keys(self.state).map(k => ({
          key: k,
          label: k
        })) : []
      }

      const childNodes = []
      //      if (parent) childNodes.push(parent)
      if (props) childNodes.push(props)
      if (state) childNodes.push(state)
      //      if (children) childNodes.push(children)

      return {
        key: self.id,
        type: 'component',
        label: self.name || self.type,
        secondaryLabel: self.type,
        meta: { component: self },
        childNodes
      }
    }
  }))
  .actions(crudActions)
  .views(self => ({
    getAllProps (props = {}) {
      const m = this.getAllPropsMappers()
      return Object.keys(this.getAllPropsMappers()).reduce((o, k) => {
        o[k] = m[k].toProps
        return o
      }, {})
    },
    getAllMappings (m = []) {
      if (self.mapping) m.push(self.mapping)
      if (self.components) {
        for (const c of self.components) {
          c.getAllMappings(m)
        }
      }
      return m
    },
    getAllMappingsBoundToMe(maps = []) {
      for(let m of this.getAllMappings()) {
        m.getAllMappingsBoundToComponent(self, maps)
      }
      return maps
    },
    getAllMappingsBoundToMyProps() {
      const maps = this.getAllMappingsBoundToMe()
      const ret = {} as Record<string, any>
      for(let m of maps) {
        const path = m.value?.fieldPath?.split('.') || []
        if(path.shift() !== 'props') continue
        if(!get(ret, path)) set(ret, path, {})
        let o = get(ret, path)
        if (!o) continue
        if(!o.$mappings) o.$mappings = []
        o.$mappings.push(m)
      }
      return ret
    },
    getAllComponents (comps = []) {
      comps.push(self)
      if (self.components) {
        for (const c of self.components) {
          c.getAllComponents(comps)
        }
      }
      return comps
    },
    getAllPropsMappers (props = {}) {
      if ((self as any).mapper) props[self.id] = (self as any).mapper
      self.components?.forEach((c) => {
        c.getAllPropsMappers(props);
      });
      return props
    },
    getCompleteFieldSet (fieldSets = {}, useSnapshot = false) {
      const env = getEnv(self)
      if (env?.completeFieldSets?.[self.id]) return env.completeFieldSets[self.id]
      if(self.mapping) {
        if(useSnapshot) dataSourcesFieldSets(self.mapping.$treenode.snapshot, fieldSets)
        else self.mapping.getCompleteFieldSet(fieldSets)
      }
      self.components?.forEach(c => {
        c.getCompleteFieldSet(fieldSets, useSnapshot)
      })
      return fieldSets
    },
    dataSourceFieldSets (dsid, fieldSet = [], isTop) {
      if (!isTop && self.parent && (self.parent as any).dataSourceFieldSets) {
        return (self.parent as any).dataSourceFieldSets(dsid, fieldSet)
      }
      if ((self as any).dataSource === dsid) {
        [...((self as any).fieldSet || [])].map(f => fieldSet.push(f))
      }
      self.components.map(c => c.dataSourceFieldSets(dsid, fieldSet, true))
      return fieldSet
    }
  }))
  .actions(self => {
    const reloadMapping = () => {
      self.mapping?.del()
      self.setDefaultMapping(self.createFromPropTypes(docGen[self.type] || {}, false))
      const newMapping = (getRoot(self) as any).newMapping(self.createFromPropTypes(docGen[self.type] || {}, true));
      self.update(
        'mapping',
        newMapping.id
      )
    }


    const updateMapping = () => {
      if (!self.mapping?.children?.length) {
        return (self as any).reloadMapping()
      }
      const doc = docGen[self.type] || {}
      self.setDefaultMapping(self.createFromPropTypes(doc, false))
      const newMapping = Mapping.create(self.createFromPropTypes(doc, true))

      if (newMapping) {
        if (self.mapping?.comp !== self) {
          self.mapping.update('comp', self.id)
        }
        checkChildrenMappings(newMapping, self.mapping)
      }
    }

    const moveObjectKeyTo = function (from, to, arr) {
      return arr.splice(to, 0, arr.splice(from, 1)[0])
    }
    const checkChildrenMappings = (newMap, oldMap) => {
      newMap.children.forEach((newNode, index) => {
        const oldNode = oldMap.children.find(c => c.key === newNode.key)
        if (!oldNode) {
          oldMap.addChild(getSnapshot(newNode))
          const children = [...getSnapshot(oldMap.children) as typeof self[]]
          moveObjectKeyTo(children.length - 1, index, children)
          oldMap.update('children', children)
        } else if (newNode.children) {
          if (oldNode.children) {
            checkChildrenMappings(newNode, oldNode)
          } else {
            oldMap.addChild(newNode.children)
          }
        }
      })
    }
    function deleteFieldFromFieldSet (item) {
      const fieldSet = self.mapping.children.find(c => c.key === 'fieldset')
      fieldSet.value.splice(fieldSet.value.indexOf(item), 1)
    }
    function updateFieldSet (fieldSet) {
      (self as any).fieldSet = fieldSet && fieldSet.map(f => Field.create(f))
    }
    function updateItem (item) {
      Object.keys(item).map(k => (self[k] = item[k]))
    }
    function swapComponents (i, j) {
      const a = i > j ? j : i
      const b = i > j ? i : j
      const newOrder = [
        ...self.components.slice(0, a),
        self.components[b],
        ...self.components.slice(a + 1, b),
        self.components[a],
        ...self.components.slice(b + 1)
      ]
      self.update('components', newOrder)
    }
    function updateProps (props) {
      if (props) self.props = { ...self.props, ...props }
    }
    function addComponent (component, preserveWH) {
      const store = getRoot(self) as IStore
      const createMappings = c => {
        let mapping
        if (c.mapping && typeof c.mapping === 'object') {
          mapping = (store as any).newMapping(c.mapping).id
        } else mapping = c.mapping
        if (c.components) {
          c.components = c.components.map(cc => createMappings(cc))
        }
        return { ...c, mapping }
      }
      const defSize = defaultSizes[component.type] || {}
      let comp = {
        ...component,
        id: component.id || uuid(),
        position: {
          x: 0,
          y: self.maxY !== null && self.maxY !== -Infinity ? self.maxY : 0,
          h: preserveWH ? component.position.h : defSize.h || 4,
          w: preserveWH
            ? component.position.w || store.settings.MAX_W || 48
            : defSize.w || store.settings.MAX_W || 48
        }
      }

      const excludeExpanding = ['Page', 'Tabs', 'Carousel', 'Conditional']

      if (
        !excludeExpanding.includes(self.type) &&
        self.position.h < comp.position.y + comp.position.h &&
        (self.parent as typeof self).type
      ) {
        (self.parent as any).expandChild(self, {
          ...self.position,
          h: comp.position.y + comp.position.h
        })
      }
      comp = createMappings(comp)
      self.components.push(comp)
      const c = self.components[self.components.length - 1]
      if (!c.mapping) c.reloadMapping()
      return c
    }
    function newMapping (reactComponent, props) {
      if (reactComponent && reactComponent.__docgenInfo) {
        self.mapping = createMappingForType(reactComponent.__docgenInfo, undefined, undefined, true)
        if (props) {
          props.forEach(p => {
            const child = self.mapping.children.find(c => c.key === p.key)
            Object.keys(p).map(k => child.update(k, toJS(p[k])))
          })
        }
      }
      return self.mapping
    }

    function expandChild (child, newChildPosition) {
      const { x, y, w, h } = newChildPosition
      const rectanglesOverlap = (x1, y1, w1, h1, x2, y2, w2, h2) => {
        const dx = x1 < x2 ? x1 + w1 - x2 : x2 + w2 - x1
        if (dx < 0) return 0
        const dy = y1 < y2 ? y1 + h1 - y2 : y2 + h2 - y1
        if (dy < 0) return 0
        return dx * dy
      }

      const conflictingComponents = self.components.filter(comp => {
        const { x: x2, y: y2, w: w2, h: h2 } = comp.position
        return (
          rectanglesOverlap(x, y, w, h, x2, y2, w2, h2) && comp.id !== child.id
        )
      })

      if (!conflictingComponents.length) {
        child.position.update(newChildPosition)
      }
    }
    function updatePosition (position, isScaling) {
      const old = { ...self.position }

      self.position.update({
        x: position.x,
        y: position.y,
        w: position.w,
        h: position.h
      })

      if (!isScaling) return

      // let maxH = self.position.h
      //      let maxW = self.position.w
      const ratioH = (self.position.h - old.h) / old.h
      //      let ratioW = old.w / self.position.w
      self.components &&
        self.components.forEach(c => {
          let { x, y, w, h } = c.position

          h = Math.round(h + h * ratioH)
          y = Math.round(y + y * ratioH)
          // if(!crop) {
          //            w = Math.round(w*ratioW) //Math.round(ch * ratioH / 2)
          //  x = Math.round(x*ratioW)
          //  }
          if (w < 1) w = 1
          if (x < 0) x = 0
          if (y < 0) y = 0
          if (h < 1) h = 1
          c.updatePosition({ ...c.position, x, y, w, h }, isScaling)
        })
    }
    function updateComponentPositions (positions, isScaling, dynamicHeight) {
      positions.forEach(p => {
        const comp = self.components.find(({ id }) => id === p.i.split(':')[0])
        if (comp) comp.updatePosition(p, isScaling)
      })
      if (dynamicHeight) {
        self.position.h = self.maxY
      }

      // if(max > this.position.h + this.position.y) this.position.h = max
    }
    function resizeW (oldVal, newVal) {
      self.position.w = ((self.position.w || 1) * newVal) / oldVal
      self.components && self.components.forEach(c => c.resizeW(oldVal, newVal))
    }
    return {
      reloadMapping,
      updateMapping,
      deleteFieldFromFieldSet,
      updateFieldSet,
      updateItem,
      swapComponents,
      updateProps,
      addComponent,
      expandChild,
      newMapping,
      updatePosition,
      updateComponentPositions,
      resizeW
    }
  })
  .preProcessSnapshot(c => {
    if (c.type === 'Tabs' && c.components && c.components.length) {
      let snap = c
      if (isStateTreeNode(snap)) snap = getSnapshot(c as any)
      return {
        ...snap,
        components: snap.components.map(child => ({
          ...child,
          position: { ...child.position, x: 0, y: 0, h: snap.position.h }
        }))
      }
    }
    return c
  })

const _Component = t.compose(
  'Component',
  Component,
  Auditable
)

export interface IComponent extends Instance<typeof _Component> {}
export default _Component;
